// Shared constants for the Agent Workspace Platform.
//
// This module is imported by the browser (web) package, so it MUST stay
// browser-safe: plain objects/arrays/string unions only, no Node built-ins,
// no server-only dependencies (drizzle-orm, fastify, pg-boss, dockerode, ...).

// ---------------------------------------------------------------------------
// Run lifecycle (FSM) — see FD §17.1
// ---------------------------------------------------------------------------

/** Every state a Run can be in. */
export const RunStatus = {
  Queued: 'queued',
  Provisioning: 'provisioning',
  Running: 'running',
  AwaitingApproval: 'awaiting_approval',
  Verifying: 'verifying',
  Completed: 'completed',
  Blocked: 'blocked',
  Failed: 'failed',
  Cancelled: 'cancelled',
  TimedOut: 'timed_out',
  /** Entered from AwaitingApproval when an `ask_human`/`request_approval`
   * run-tool call outlives the 15-minute live-wait window: the sandbox is
   * torn down and the run's snapshot moves to `run_pauses` until a decision
   * resumes it (-> Queued) or the pause expires (-> Blocked). */
  Paused: 'paused',
  /** The run's work was taken over by a new run on another agent
   * (docs/plans/2026-10-02-checkpoints-contract.md §5): that child run has
   * `parent_run_id` = this run and `attempt + 1`. Terminal. Entered from
   * Running (usage limit), Failed/Blocked/Paused (manual or quota handoff)
   * and Queued (connection exhausted before the run ever started). */
  HandedOff: 'handed_off',
} as const

export type RunStatus = (typeof RunStatus)[keyof typeof RunStatus]

export const RUN_STATUSES = Object.values(RunStatus) as RunStatus[]

/** Terminal states: once entered, a run never transitions again. */
export const TERMINAL_RUN_STATUSES: readonly RunStatus[] = [
  RunStatus.Completed,
  RunStatus.Blocked,
  RunStatus.Failed,
  RunStatus.Cancelled,
  RunStatus.TimedOut,
  RunStatus.HandedOff,
]

/**
 * Allowed state transitions, mirroring the FD §17.1 state diagram.
 *
 * ```text
 * Queued --> Provisioning
 * Provisioning --> Running
 * Running --> AwaitingApproval
 * AwaitingApproval --> Running
 * Running --> Verifying
 * Verifying --> Completed
 * Running --> Blocked | Failed | Cancelled | TimedOut
 * ```
 */
export const RUN_STATE_TRANSITIONS: Readonly<
  Record<RunStatus, readonly RunStatus[]>
> = {
  // Paused/HandedOff from Queued: the run's connection is exhausted before
  // it ever started (checkpoints contract §5) — wait, or hand the step to a
  // fallback agent.
  [RunStatus.Queued]: [RunStatus.Provisioning, RunStatus.Cancelled, RunStatus.Paused, RunStatus.HandedOff],
  // Queued from Provisioning/Running/AwaitingApproval: crash recovery (the
  // worker holding the lease died; the API re-enqueues from the last
  // checkpoint). Paused from Running: usage limit, `onQuota.mode` 'wait'.
  [RunStatus.Provisioning]: [
    RunStatus.Running,
    RunStatus.Failed,
    RunStatus.Cancelled,
    RunStatus.Queued,
  ],
  [RunStatus.Running]: [
    RunStatus.AwaitingApproval,
    RunStatus.Verifying,
    RunStatus.Blocked,
    RunStatus.Failed,
    RunStatus.Cancelled,
    RunStatus.TimedOut,
    RunStatus.Paused,
    RunStatus.HandedOff,
    RunStatus.Queued,
  ],
  [RunStatus.AwaitingApproval]: [
    RunStatus.Running,
    RunStatus.Paused,
    RunStatus.Cancelled,
    RunStatus.TimedOut,
    RunStatus.Queued,
  ],
  [RunStatus.Verifying]: [
    RunStatus.Completed,
    RunStatus.Failed,
    RunStatus.Blocked,
  ],
  [RunStatus.Completed]: [],
  // Failed/Blocked are terminal for every automatic path; only a person's
  // "resume" / "hand off" on a run with a checkpoint reopens them
  // (checkpoints contract §5).
  [RunStatus.Blocked]: [RunStatus.Queued, RunStatus.HandedOff],
  [RunStatus.Failed]: [RunStatus.Queued, RunStatus.HandedOff],
  [RunStatus.Cancelled]: [],
  [RunStatus.TimedOut]: [],
  [RunStatus.HandedOff]: [],
  /** Resumed (a decision was made, or `resume_after` passed) -> Queued;
   * cancelled while paused -> Cancelled; the pause expired unanswered ->
   * Blocked; handed to another agent -> HandedOff. */
  [RunStatus.Paused]: [RunStatus.Queued, RunStatus.Cancelled, RunStatus.Blocked, RunStatus.HandedOff],
}

/** Pure guard used by both API and worker before persisting a transition. */
export function canTransitionRunStatus(
  from: RunStatus,
  to: RunStatus
): boolean {
  // TODO: consider allowing a same-state no-op transition for idempotent retries
  return RUN_STATE_TRANSITIONS[from]?.includes(to) ?? false
}

// ---------------------------------------------------------------------------
// Tool classification — see FD §12.2
// ---------------------------------------------------------------------------

export const ToolClassification = {
  ReadOnly: 'read_only',
  Write: 'write',
  Destructive: 'destructive',
  Privileged: 'privileged',
  ExternalCommunication: 'external_communication',
} as const

export type ToolClassification =
  (typeof ToolClassification)[keyof typeof ToolClassification]

export const TOOL_CLASSIFICATIONS = Object.values(
  ToolClassification
) as ToolClassification[]

// ---------------------------------------------------------------------------
// Sandbox profile types — see FD §18
// ---------------------------------------------------------------------------

export const SandboxProfileType = {
  /** §18.1 — no sandbox, restricted process with no host filesystem access. */
  None: 'none',
  /** §18.2 — dedicated system user, working directory, resource limits. */
  ProcessIsolation: 'process_isolation',
  /** §18.3 — ephemeral, locked-down Docker container. */
  EphemeralContainer: 'ephemeral_container',
} as const

export type SandboxProfileType =
  (typeof SandboxProfileType)[keyof typeof SandboxProfileType]

export const SANDBOX_PROFILE_TYPES = Object.values(
  SandboxProfileType
) as SandboxProfileType[]

// ---------------------------------------------------------------------------
// Workload — how heavy a role's runs are on the worker machine
// ---------------------------------------------------------------------------

/**
 * Set per role version. 'heavy' roles build and test code in their sandbox
 * (Developer, Tester); 'light' roles mostly wait on the model (Spec Writer,
 * Reviewer, ...). The worker gives each class its own concurrency slots and
 * sandbox size, so light runs never queue behind a long build.
 */
export const Workload = {
  Light: 'light',
  Heavy: 'heavy',
} as const

export type Workload = (typeof Workload)[keyof typeof Workload]

export const WORKLOADS = Object.values(Workload) as Workload[]

/** pg-boss queue a run's execute job goes to; one per workload class. */
export function runExecuteQueue(workload: Workload): string {
  return `run.execute.${workload}`
}

/** Human-readable metadata for each sandbox profile type (FD §18). */
export const SANDBOX_PROFILES: Readonly<
  Record<SandboxProfileType, { label: string; description: string }>
> = {
  [SandboxProfileType.None]: {
    label: 'None',
    description:
      'No sandbox — a restricted process with no host filesystem access (FD §18.1).',
  },
  [SandboxProfileType.ProcessIsolation]: {
    label: 'Process isolation',
    description:
      'A dedicated system user, working directory and resource limits (FD §18.2).',
  },
  [SandboxProfileType.EphemeralContainer]: {
    label: 'Ephemeral container',
    description: 'An ephemeral, locked-down Docker container (FD §18.3).',
  },
}

// ---------------------------------------------------------------------------
// Audit event types — see FD §24.2
// ---------------------------------------------------------------------------

export const AuditEventType = {
  RoleCreated: 'role.created',
  RoleUpdated: 'role.updated',
  RoleVersionRestored: 'role.version_restored',
  PolicyUpdated: 'policy.updated',
  SkillImported: 'skill.imported',
  SkillActivated: 'skill.activated',
  SkillBlocked: 'skill.blocked',
  McpServiceRegistered: 'mcp_service.registered',
  McpPermissionUpdated: 'mcp_service.permission_updated',
  CredentialConnected: 'credential.connected',
  CredentialRevoked: 'credential.revoked',
  TaskCreated: 'task.created',
  RunStarted: 'run.started',
  RunStopped: 'run.stopped',
  RunApproved: 'run.approved',
  RunRejected: 'run.rejected',
  RunRetried: 'run.retried',
  MemoryProposed: 'memory.proposed',
  MemoryAccepted: 'memory.accepted',
  MemoryRejected: 'memory.rejected',
  ArtifactDownloaded: 'artifact.downloaded',
  UserRoleGranted: 'user.role_granted',
  UserRoleRevoked: 'user.role_revoked',
  WorkspaceSettingsUpdated: 'workspace.settings_updated',
} as const

export type AuditEventType =
  (typeof AuditEventType)[keyof typeof AuditEventType]

export const AUDIT_EVENT_TYPES = Object.values(
  AuditEventType
) as AuditEventType[]

// ---------------------------------------------------------------------------
// User roles — see FD §7.1
// ---------------------------------------------------------------------------

export const UserRoleName = {
  WorkspaceOwner: 'workspace_owner',
  Administrator: 'administrator',
  Operator: 'operator',
  Approver: 'approver',
  Viewer: 'viewer',
} as const

export type UserRoleName = (typeof UserRoleName)[keyof typeof UserRoleName]

export const USER_ROLE_NAMES = Object.values(UserRoleName) as UserRoleName[]

// ---------------------------------------------------------------------------
// Executor types — see FD §9.1
// ---------------------------------------------------------------------------

export const ExecutorType = {
  CodexCli: 'codex_cli',
  ClaudeCli: 'claude_cli',
  OpenAiCompatibleApi: 'openai_compatible_api',
  OpenHandsAgentServer: 'openhands_agent_server',
  GeminiCli: 'gemini_cli',
  LocalModel: 'local_model',
  CustomScript: 'custom_script',
} as const

export type ExecutorType = (typeof ExecutorType)[keyof typeof ExecutorType]

export const EXECUTOR_TYPES = Object.values(ExecutorType) as ExecutorType[]

// ---------------------------------------------------------------------------
// Approval types — see FD §21.1
// ---------------------------------------------------------------------------

export const ApprovalType = {
  BudgetOrTimeExceeded: 'budget_or_time_exceeded',
  PrivilegedOrDestructiveTool: 'privileged_or_destructive_tool',
  NewNetworkDomain: 'new_network_domain',
  BranchPush: 'branch_push',
  PullRequestCreateOrReady: 'pull_request_create_or_ready',
  FollowUpAgent: 'follow_up_agent',
  /**
   * @deprecated Memory moved to the knowledge base (migration 0019): no run
   * raises this any more. Kept only so historical approvals still display.
   */
  PersistentMemorySave: 'persistent_memory_save',
  DeploymentMergeOrRelease: 'deployment_merge_or_release',
  /** A human state in a task's workflow (no run involved) — see types/workflow.ts. */
  WorkflowGate: 'workflow_gate',
  /** The `ask_human` run-tool: answered with free text, not approve/reject. */
  AgentQuestion: 'agent_question',
  /** The `request_approval` run-tool: approve/reject, like any other approval — except a reject sends the run back to `running` instead of failing it. */
  AgentRequest: 'agent_request',
} as const

export type ApprovalType = (typeof ApprovalType)[keyof typeof ApprovalType]

export const APPROVAL_TYPES = Object.values(ApprovalType) as ApprovalType[]

/**
 * Policy/role approval actions that no longer exist. `persistent_memory_save`
 * became the knowledge base's folder policy `proposal` (migration 0019); the
 * API drops these when it parses a project policy or saves a role.
 */
export const RETIRED_APPROVAL_ACTIONS: readonly string[] = ['persistent_memory_save']

export const ApprovalStatus = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
  Expired: 'expired',
  Stale: 'stale', // action changed after approval was granted
} as const

export type ApprovalStatus =
  (typeof ApprovalStatus)[keyof typeof ApprovalStatus]

export const APPROVAL_STATUSES = Object.values(
  ApprovalStatus
) as ApprovalStatus[]

/** Default approval expiry window, in seconds (FD §21.2 `default_expiry`). */
export const APPROVAL_EXPIRY_SECONDS = 3600

/**
 * How long a paused run's snapshot (`run_pauses`) and its extended approval
 * stay open before the pause expires (plan 04 §2.6): 7 days. Also used to
 * extend the triggering approval's `expiresAt` the moment a run is paused,
 * so the two expire together.
 */
export const RUN_PAUSE_EXPIRY_SECONDS = 7 * 24 * 3600

// ---------------------------------------------------------------------------
// Chat in Goblic (docs/plans/2026-09-30-runners/07-chat.md) — the handful of
// numbers the API (queues a turn, mints its token) and the worker (runs it)
// both need to agree on. Kept here rather than duplicated so neither side
// drifts from the other.
// ---------------------------------------------------------------------------

/** pg-boss queue a posted chat message's turn goes to (07 §2.1); one queue, not per-workload like runs. */
export const CHAT_TURN_QUEUE = 'chat.turn'

/**
 * How long a turn may sit `running` before periodic-maintenance.ts treats it
 * as abandoned (its worker crashed or was redeployed mid-turn) and fails it
 * itself — 07 QA #2. Also the queue's own `expireInSeconds` (QA #1): a turn
 * past this point is dead either way, so pg-boss should never retry it.
 * 5 minutes past `CHAT_TURN_MAX_DURATION_MS` (20 min), so a turn that's
 * merely still running its own timeout never gets swept prematurely.
 */
export const CHAT_TURN_STUCK_AFTER_MS = 25 * 60 * 1000

/**
 * Options of the `run.execute.*` queues. The API and the worker both declare these queues and must agree: a
 * run-execute job stays `active` for the whole run, including long waits on a human's answer, so pg-boss's default
 * 15-minute expiry would redeliver it mid-run (2026-10-04: duplicate delivery of a run waiting on a question).
 * The run's own lease/heartbeat, not pg-boss's expiry, detects a dead worker.
 */
export const RUN_EXECUTE_QUEUE_OPTIONS = {
  expireInSeconds: 23 * 60 * 60,
} as const

/**
 * `chat.turn` queue options the API (creates the queue) and the worker
 * (creates/consumes it) must agree on exactly (07 QA #1): a turn may take up
 * to 20 minutes, including time spent waiting on a confirmation card, so
 * pg-boss's own default retry/expiry (2 retries, 15-minute expiry) would
 * both time out a still-legitimate turn and then *retry* it — running the
 * same turn twice. `retryLimit: 0` because a chat turn is never safe to
 * retry (it already talked to a model, posted messages, maybe ran a write
 * tool); `expireInSeconds` matches `CHAT_TURN_STUCK_AFTER_MS` so pg-boss's
 * own notion of "stuck" lines up with periodic-maintenance.ts's.
 */
export const CHAT_TURN_QUEUE_OPTIONS = {
  retryLimit: 0,
  expireInSeconds: CHAT_TURN_STUCK_AFTER_MS / 1000,
} as const

/** Per-turn budget (07 §2.6): smaller than a run's, since a turn is one runner process answering one message. */
export const CHAT_TURN_BUDGET = {
  maxModelCalls: 30,
  maxModelTokens: 400_000,
} as const

/** A turn's wall-clock ceiling (07 §2.3): includes any time spent waiting on a confirmation card. */
export const CHAT_TURN_MAX_DURATION_MS = 20 * 60 * 1000

/** How often the worker checks whether the user cancelled the running turn (07 QA #5). */
export const CHAT_CANCEL_POLL_MS = 3000

/** How long a chat's `kind: 'chat'` API token lives — just over one turn's own max duration. */
export const CHAT_TOKEN_TTL_MS = 20 * 60 * 1000

/** `kind: 'chat'` API tokens older than this are deleted by periodic-maintenance.ts (07 §2.7's own cleanup note; QA #2) — expiry already makes them unusable, this just keeps the table from growing forever. */
export const CHAT_TOKEN_MAX_AGE_MS = 24 * 60 * 60 * 1000

/** A turn's resumable session transcript (`chat_conversations.session_blob`) over this size is never stored (07 §2.5's own "max 5 MB"; QA #12) — the next turn just falls back to replaying `chat_messages` instead. */
export const CHAT_SESSION_BLOB_MAX_BYTES = 5 * 1024 * 1024

/** How long a write/destructive confirmation card waits for the user before the worker blocks it itself (07 §2.3). Same figure as the run guard's own `ask` wait (00-overzicht.md). */
export const CHAT_CONFIRMATION_WAIT_MS = 15 * 60 * 1000
/** How often the worker polls a pending confirmation's status. */
export const CHAT_CONFIRMATION_POLL_MS = 2000

/** A conversation's running token total past which new messages are refused (07 §2.6). */
export const CHAT_CONVERSATION_TOKEN_CAP = 3_000_000

/** Default conservative token budget (UTF-8 byte upper bound) for summary plus recent chat history.
 * The API accepts CHAT_CONTEXT_TOKEN_BUDGET to override it. */
export const CHAT_CONTEXT_TOKEN_BUDGET_DEFAULT = 40_000

/**
 * How often a streaming turn (the no-sandbox Pi-ai/Claude-Code chat paths)
 * flushes its growing assistant text to the API as a `chat_messages` row
 * update, instead of writing on every delta event — a model can emit many
 * text deltas per second, and the existing SSE stream (routes/chat/
 * stream.ts) already dedupes by a row's `updatedAt`, so this is purely a
 * write-rate cap, not a correctness requirement.
 */
export const CHAT_ASSISTANT_DELTA_THROTTLE_MS = 150

/** Per chat turn, independent of a role's own run-tools limits (05): how many `web_search`/`web_fetch` calls a turn may make before the tool itself starts refusing (coordinator note, 2026-10-01 runners/chat no-sandbox rework). `web_fetch` is always offered; `web_search` only when the workspace has an active search connection. */
export const CHAT_TURN_WEB_LIMITS = { webSearches: 10, webFetches: 20 } as const

// ---------------------------------------------------------------------------
// Task sources — see FD §16.1
// ---------------------------------------------------------------------------

export const TaskSource = {
  Manual: 'manual',
  PlaneWebhook: 'plane_webhook',
  GitHubWebhook: 'github_webhook',
  ScheduleOrCron: 'schedule_or_cron',
  ApiCall: 'api_call',
  RunFollowUp: 'run_follow_up',
  /** Created as a subtask of another task (docs/plans/2026-10-03-subtaken.md). */
  Subtask: 'subtask',
} as const

export type TaskSource = (typeof TaskSource)[keyof typeof TaskSource]

export const TASK_SOURCES = Object.values(TaskSource) as TaskSource[]

// ---------------------------------------------------------------------------
// Risk levels — used by roles, tasks and approvals
// ---------------------------------------------------------------------------

export const RiskLevel = {
  Low: 'low',
  Medium: 'medium',
  High: 'high',
  Critical: 'critical',
} as const

export type RiskLevel = (typeof RiskLevel)[keyof typeof RiskLevel]

export const RISK_LEVELS = Object.values(RiskLevel) as RiskLevel[]

// ---------------------------------------------------------------------------
// MCP transport types — see FD §12.1
// ---------------------------------------------------------------------------

export const McpTransportType = {
  Stdio: 'stdio',
  /** Streamable HTTP. */
  Http: 'http',
  /** The older HTTP + server-sent events transport. */
  Sse: 'sse',
  WebSocket: 'websocket',
} as const

export type McpTransportType =
  (typeof McpTransportType)[keyof typeof McpTransportType]

export const MCP_TRANSPORT_TYPES = Object.values(
  McpTransportType
) as McpTransportType[]

// ---------------------------------------------------------------------------
// Health status — used by executors and MCP services
// ---------------------------------------------------------------------------

export const HealthStatus = {
  Unknown: 'unknown',
  Healthy: 'healthy',
  Degraded: 'degraded',
  Unhealthy: 'unhealthy',
} as const

export type HealthStatus = (typeof HealthStatus)[keyof typeof HealthStatus]

export const HEALTH_STATUSES = Object.values(HealthStatus) as HealthStatus[]

// ---------------------------------------------------------------------------
// Run event streaming & worker lease tuning — see FD §17.2, §22.4, §24.1, §26
// ---------------------------------------------------------------------------

/** Max number of run events flushed to a client in a single SSE batch. */
export const MAX_RUN_EVENTS_PER_SSE_BATCH = 50

/** Keep-alive comment interval for SSE connections, in ms (FD §22.4 latency budget). */
export const HEARTBEAT_INTERVAL_MS = 15_000

/** Worker lease duration for an in-flight run, in seconds (FD §26 crash recovery). */
export const LEASE_DURATION_SECONDS = 60

/**
 * Where a run's skills are placed in the sandbox (HOME is /tmp there), the
 * same path for every runner (01 §2.3): Claude Code's Skill tool is off
 * (`--setting-sources=` is empty, see 02), so it no longer discovers
 * `~/.claude/skills` by itself either — every runner is pointed at its
 * skills by the "Skills" section of the composed context instead. Outside
 * the checked-out repository, so skills never end up in a commit.
 *
 * Takes `executorKind` only to keep existing call sites unchanged; every
 * kind resolves to the same path.
 */
export function sandboxSkillsDir(executorKind: string): string {
  void executorKind
  return '/tmp/.goblic-skills'
}

// ---------------------------------------------------------------------------
// Managed Pi extension catalog — see docs/plans/2026-09-30-runners/
// 03-pi-runner.md §2.2 and 05-web-en-rolinstellingen.md §2.1
// ---------------------------------------------------------------------------

/**
 * Ids a role's `runSettings.piExtensions` may reference — the `id`s declared
 * in `docker/agent-image/runners/pi/extensions.json` (03's managed list).
 *
 * This is a hand-kept mirror, not a runtime read of that file: the API
 * (`RoleService.assertNarrowsWorkspaceCeiling`) is a server bundle with no
 * guaranteed access to `docker/agent-image/` at runtime, and a repo-relative
 * `fs.readFile` across that packaging boundary is exactly the kind of thing
 * that works in a dev checkout and silently breaks once built into an image.
 * `constants/pi-extensions-catalog.test.ts` reads the real JSON file (it can
 * — tests run from the repo checkout) and asserts its `id`s equal this array
 * exactly, so the two can never drift unnoticed: 03 adding an approved
 * extension must update both the JSON file and this constant in the same
 * change, or the test fails.
 */
export const PI_EXTENSION_CATALOG_IDS: readonly string[] = []
